import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router";
import { useAuthStore } from "../../stores/authStore";
import {
  logoutUser,
  fetchUserPermissions,
  isSessionTokenExpired,
  isSessionTokenExpiringSoon,
  isRefreshTokenExpired,
  refreshAccessToken,
  handleSessionExpiration,
  resetSessionExpiredAlert,
} from "../../api/frameworksApi";
import { Button } from "./ui/button";

/* ── Configuration ────────────────────────────────────────────────────── */
/** Inactivity threshold before the "Are you still there?" popup appears (5 minutes). */
const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000;
const POPUP_COUNTDOWN_SECONDS = 30;

/** User interactions that reset the inactivity timer. */
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "pointerdown",
];

export type SessionPopupReason = "inactivity" | "expired";

export function SessionTimeoutManager() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const [popupReason, setPopupReason] = useState<SessionPopupReason | null>(null);
  const [countdown, setCountdown] = useState(POPUP_COUNTDOWN_SECONDS);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isDismissedRef = useRef(false);
  const stayLoggedInButtonRef = useRef<HTMLButtonElement>(null);

  /* ── Perform Logout & Redirect ──────────────────────────────────────── */
  const doLogout = useCallback(
    async (reason: string) => {
      setPopupReason(null);
      resetSessionExpiredAlert();
      sessionStorage.removeItem("accessTokenRefreshCount");
      console.log(`[SessionTimeoutManager] Logging out: ${reason}`);
      try {
        await logoutUser();
      } catch (err) {
        console.warn("[SessionTimeoutManager] logoutUser error:", err);
      } finally {
        useAuthStore.getState().logout();
        navigate("/login", { replace: true });
      }
    },
    [navigate]
  );

  const [isRefreshing, setIsRefreshing] = useState(false);
  const isRefreshingRef = useRef(false);

  /* ── Stay Logged In handler ─────────────────────────────────────────── */
  const handleStayLoggedIn = useCallback(async (e?: React.SyntheticEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Prevent duplicate calls if already processing
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    setIsRefreshing(true);

    // Synchronously stop the countdown to eliminate race conditions
    isDismissedRef.current = true;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    resetSessionExpiredAlert();
    setPopupReason(null);
    setCountdown(POPUP_COUNTDOWN_SECONDS);

    // Call refresh API to get a new access token
    try {
      const newAccessToken = await refreshAccessToken();
      if (!newAccessToken) {
        // If refresh failed (e.g., refresh token also expired), log out to login page
        console.warn("[SessionTimeoutManager] Failed to obtain new access token on stay, logging out");
        void doLogout("Refresh token expired or invalid");
        return;
      }
      console.log("[SessionTimeoutManager] Successfully refreshed access token on stay");
    } catch (err) {
      console.warn("[SessionTimeoutManager] Error refreshing token on stay:", err);
      void doLogout("Refresh token error");
      return;
    } finally {
      isRefreshingRef.current = false;
      setIsRefreshing(false);
    }

    // Ping user permissions
    try {
      await fetchUserPermissions();
    } catch (err) {
      console.warn("[SessionTimeoutManager] Session keepalive ping error:", err);
    }
  }, [doLogout]);

  /* ── Unified Session, Inactivity, and Modal Lifecycle Effect ───────────── */
  useEffect(() => {
    // 1. If on login page, clear popup and skip active tracking
    if (location.pathname === "/login") {
      setPopupReason(null);
      return;
    }

    // 2. Session / 401 Expired Listener (active across all non-login pages)
    const handleSessionExpired = (e: Event) => {
      console.warn("[SessionTimeoutManager] Received session-expired event:", (e as CustomEvent)?.detail);
      setPopupReason("expired");
    };
    window.addEventListener("session-expired", handleSessionExpired);

    // 3. If unauthenticated, do not track inactivity or token expiry, but PRESERVE "expired" popup
    if (!isAuthenticated) {
      if (popupReason && popupReason !== "expired") {
        setPopupReason(null);
      }
      return () => {
        window.removeEventListener("session-expired", handleSessionExpired);
      };
    }


    const checkToken = async () => {
      const hasValidRefresh =
        useAuthStore.getState().getRefreshToken() && !isRefreshTokenExpired();

      if (!hasValidRefresh) {
        if (isSessionTokenExpired()) {
          console.warn("[SessionTimeoutManager] Token expired and no valid refresh token available - logging out");
          handleSessionExpiration();
        }
        return;
      }

      const refreshCount = Number(sessionStorage.getItem("accessTokenRefreshCount") || "0");

      if (refreshCount === 0) {
        // ── 1ST TOKEN EXPIRY: completely silent background process (10 seconds before expiry) ──
        if (isSessionTokenExpiringSoon(10)) {
          console.log("[SessionTimeoutManager] 1st access token expiring in 10s, performing silent background refresh...");
          const newAccessToken = await refreshAccessToken();
          if (newAccessToken) {
            sessionStorage.setItem("accessTokenRefreshCount", "1");
            console.log("[SessionTimeoutManager] 1st background refresh succeeded, count set to 1");
            return;
          }
        }
      } else {
        // ── 2ND TOKEN EXPIRY: ask user before 1 min (60 seconds) to Stay or Logout ──
        if (isSessionTokenExpiringSoon(10)) {
          if (!popupReason) {
            console.warn("[SessionTimeoutManager] 2nd access token expiring in 1 min, prompting user to Stay or Logout");
            isDismissedRef.current = false;
            setCountdown(10);
            setPopupReason("expired");
          }
          return;
        }
      }

      // If token is already fully expired and not renewed: immediately logout
      if (isSessionTokenExpired()) {
        console.warn("[SessionTimeoutManager] Session expired according to JWT exp timestamp - logging out");
        handleSessionExpiration();
      }
    };
    void checkToken();
    const tokenInterval = setInterval(() => {
      void checkToken();
    }, 5000);

    // 5. Manage Modal State & Behavior
    let originalPointerEvents: string | null = null;
    let focusTimer: ReturnType<typeof setTimeout> | null = null;
    let handleKeyDown: ((e: KeyboardEvent) => void) | null = null;
    let inactivityTimer: ReturnType<typeof setTimeout> | null = null;
    let onActivity: (() => void) | null = null;

    if (popupReason) {
      // Ensure body pointer-events is auto when modal is visible
      originalPointerEvents = document.body.style.pointerEvents;
      document.body.style.pointerEvents = "auto";

      // Auto-focus "Stay Logged In" button
      focusTimer = setTimeout(() => {
        stayLoggedInButtonRef.current?.focus();
      }, 50);

      // Capture Enter/Space/Escape keyboard events to stay logged in
      handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " " || e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          void handleStayLoggedIn();
        }
      };
      window.addEventListener("keydown", handleKeyDown, true);

      // Run countdown timer for the active popup
      isDismissedRef.current = false;
      const initialCountdown = popupReason === "inactivity" ? POPUP_COUNTDOWN_SECONDS : 60;
      setCountdown(initialCountdown);

      intervalRef.current = setInterval(() => {
        if (isDismissedRef.current) return;

        setCountdown((prev) => {
          if (prev <= 1) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            if (!isDismissedRef.current) {
              isDismissedRef.current = true;
              setTimeout(() => {
                void doLogout(
                  popupReason === "inactivity"
                    ? "Inactivity countdown expired"
                    : "Session token expired after countdown"
                );
              }, 0);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      // Modal is closed: track user activity and trigger popup after 2 minutes of inactivity
      const resetInactivityTimer = () => {
        if (inactivityTimer) clearTimeout(inactivityTimer);

        inactivityTimer = setTimeout(() => {
          if (useAuthStore.getState().isAuthenticated) {
            console.warn("[SessionTimeoutManager] 5 minutes inactivity reached, showing popup");
            isDismissedRef.current = false;
            setCountdown(POPUP_COUNTDOWN_SECONDS);
            setPopupReason("inactivity");
          }
        }, INACTIVITY_TIMEOUT_MS);
      };

      resetInactivityTimer();

      onActivity = () => {
        resetInactivityTimer();
      };

      for (const event of ACTIVITY_EVENTS) {
        window.addEventListener(event, onActivity, { passive: true });
      }
    }

    // 6. Cleanup all active listeners, intervals, and timers
    return () => {
      window.removeEventListener("session-expired", handleSessionExpired);
      clearInterval(tokenInterval);

      if (originalPointerEvents !== null) {
        document.body.style.pointerEvents = originalPointerEvents;
      }

      if (focusTimer) clearTimeout(focusTimer);
      if (handleKeyDown) {
        window.removeEventListener("keydown", handleKeyDown, true);
      }

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      if (inactivityTimer) clearTimeout(inactivityTimer);
      if (onActivity) {
        for (const event of ACTIVITY_EVENTS) {
          window.removeEventListener(event, onActivity);
        }
      }
    };
  }, [isAuthenticated, location.pathname, popupReason, doLogout, handleStayLoggedIn]);

  if (!popupReason || typeof document === "undefined") return null;

  // Circular progress calculations for the 76px viewBox (radius 32)
  const radius = 32;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (circumference * countdown) / POPUP_COUNTDOWN_SECONDS;

  return createPortal(
    <div
      className="pointer-events-auto fixed inset-0 z-[999999] flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity animate-in fade-in duration-200"
      style={{ pointerEvents: "auto" }}
      onClick={(e) => {
        // Clicking backdrop safely keeps user logged in
        void handleStayLoggedIn(e);
      }}
    >
      <div
        className="
          pointer-events-auto relative
          w-full max-w-[440px] mx-4 rounded-2xl overflow-hidden
          bg-card text-card-foreground shadow-2xl
          border border-border
          animate-in zoom-in-95 duration-200
        "
        style={{ pointerEvents: "auto" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-dialog-title"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Accent top gradient bar */}
        <div
          className={`h-1.5 w-full ${popupReason === "inactivity"
            ? "bg-primary"
            : "bg-destructive"
            }`}
        />

        <div className="px-8 pt-7 pb-8 text-center">
          {/* Top Badge: Animated Circular Countdown or Shield Icon */}
          {popupReason === "inactivity" ? (
            <div className="relative mx-auto mb-4 w-20 h-20 flex items-center justify-center">
              {/* Background ring */}
              <svg className="w-20 h-20 -rotate-90" viewBox="0 0 76 76">
                <circle
                  cx="38"
                  cy="38"
                  r={radius}
                  className="text-muted/60 stroke-current"
                  strokeWidth="5"
                  fill="transparent"
                />
                {/* Animated countdown ring */}
                <circle
                  cx="38"
                  cy="38"
                  r={radius}
                  className={`stroke-current transition-all duration-1000 ease-linear ${countdown <= 10
                    ? "text-destructive"
                    : "text-primary"
                    }`}
                  strokeWidth="5"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                  fill="transparent"
                />
              </svg>
              {/* Center Counter Display */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span
                  className={`text-2xl font-black tabular-nums transition-colors duration-300 leading-tight ${countdown <= 10
                    ? "text-destructive animate-pulse"
                    : "text-foreground"
                    }`}
                >
                  {countdown}
                </span>
                <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground -mt-0.5">
                  sec
                </span>
              </div>
            </div>
          ) : (
            <div className="mx-auto mb-5 flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 text-destructive">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
          )}

          {/* Title */}
          <h2
            id="session-dialog-title"
            className="text-xl font-bold text-foreground mb-2"
          >
            {popupReason === "inactivity" ? "Are you still there?" : "Session Inactive"}
          </h2>

          {/* Description */}
          <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
            {popupReason === "inactivity" ? (
              <>
                {"You've been inactive. For your security, you'll be logged out in "}
                <span
                  className={`font-bold tabular-nums text-base ${countdown <= 10 ? "text-destructive" : "text-primary"
                    }`}
                >
                  {countdown}s
                </span>
                .
              </>
            ) : (
              <>
                {"Your session access token is about to expire in "}
                <span
                  className={`font-bold tabular-nums text-base ${countdown <= 10 ? "text-destructive" : "text-primary"
                    }`}
                >
                  {countdown}s
                </span>
                {". Would you like to stay logged in or logout?"}
              </>
            )}
          </p>

          {/* Progress bar for visual countdown draining */}
          <div className="w-full bg-muted rounded-full h-1.5 mb-6 overflow-hidden">
            <div
              className={`h-full transition-all duration-1000 ease-linear rounded-full ${countdown <= 10
                ? "bg-destructive"
                : "bg-primary"
                }`}
              style={{
                width: `${(countdown / (popupReason === "inactivity" ? POPUP_COUNTDOWN_SECONDS : 60)) * 100}%`,
              }}
            />
          </div>

          {/* Buttons: Always show Logout and Stay Logged In */}
          <div className="flex gap-3 justify-center">
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={(e) => {
                e.stopPropagation();
                isDismissedRef.current = true;
                if (intervalRef.current) {
                  clearInterval(intervalRef.current);
                  intervalRef.current = null;
                }
                void doLogout(
                  popupReason === "inactivity"
                    ? "User chose to logout from inactivity prompt"
                    : "User clicked Logout from session expired prompt"
                );
              }}
              onPointerDown={(e) => e.stopPropagation()}
              className="flex-1 rounded-xl h-11 text-sm font-semibold cursor-pointer"
            >
              Logout
            </Button>
            <Button
              ref={stayLoggedInButtonRef}
              type="button"
              variant="default"
              size="lg"
              disabled={isRefreshing}
              onClick={handleStayLoggedIn}
              onPointerDown={(e) => e.stopPropagation()}
              className="flex-1 rounded-xl h-11 text-sm font-semibold shadow-md hover:shadow-lg cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isRefreshing ? "Refreshing..." : `Stay Logged In (${countdown}s)`}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
